import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { DashboardSkeleton } from "../components/DashboardSkeleton";
import ReworkQueueWidget from "../components/ReworkQueueWidget";
import { IncompleteSalesWidget } from "../components/IncompleteSalesWidget";
import BusinessStatusDistribution from "../components/BusinessStatusDistribution";
import ProductKindComparison from "../components/ProductKindComparison";
import GeneralStatusDistribution from "../components/GeneralStatusDistribution";
import RoleDashboardWidget from "../components/RoleDashboardWidget";
import ChannelProductMatrix from "../components/ChannelProductMatrix";
import AgencyProgressCards from "../components/AgencyProgressCards";
import {
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  Plus,
  ArrowRight,
  RefreshCw,
  Users,
  Phone,
  Zap,
  Activity,
  TrendingUp,
  AlertTriangle,
  Calendar,
  Upload,
  Award,
  Ban,
  PowerOff,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const EUROCALL_LOGO =
  "https://customer-assets.emergentagent.com/job_b17cad2c-20e2-43f4-94ab-e2a3873cd763/artifacts/ua87jms9_logo%20eurocall2.png";

const Dashboard = () => {
  const { user, hasRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fuzzyStats, setFuzzyStats] = useState({ auto_resolvable_count: 0, total_groups: 0 });
  // Iter32 P4 — "View as agent" toggle (admin/BO only)
  const [agents, setAgents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [asAgentId, setAsAgentId] = useState("");
  const [agentPerspective, setAgentPerspective] = useState("commercial");
  const isAdminView = hasRole(["admin", "backoffice"]);

  const fetchStats = async (overrideAgentId, forceRefresh = false) => {
    try {
      const id = overrideAgentId !== undefined ? overrideAgentId : asAgentId;
      const params = new URLSearchParams();
      if (id) params.set("as_agent", id);
      if (forceRefresh) params.set("refresh", "true");
      const qs = params.toString();
      const url = `${API_URL}/dashboard/stats${qs ? `?${qs}` : ""}`;
      const response = await axios.get(url, { timeout: 120000 });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Eroare la încărcarea statisticilor");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const r = await axios.get(`${API_URL}/agents?limit=500`);
      setAgents(r.data?.data || r.data?.items || []);
    } catch (e) { /* silent */ }
  };

  const fetchStatsWithPerspective = async (perspective) => {
    setAgentPerspective(perspective);
    try {
      const params = new URLSearchParams();
      if (asAgentId) params.set("as_agent", asAgentId);
      params.set("agent_perspective", perspective);
      const response = await axios.get(`${API_URL}/dashboard/stats?${params.toString()}`);
      setStats(response.data);
    } catch (error) {
      toast.error("Eroare la încărcarea statisticilor");
    }
  };

  // Iter53 P25 — fetch agencies list for the role dashboard target picker
  const fetchAgencies = async () => {
    try {
      const r = await axios.get(`${API_URL}/agencies`);
      const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
      setAgencies(list);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    const init = async () => {
      await fetchStats();
      // Iter32 P4 — load agents list once for the View-as selector (admin/BO only)
      // Iter53 P25 — also load for non-admin (RoleDashboardWidget needs them for target picker)
      await fetchAgents();
      // Iter53 P25 — load agencies list for role dashboard target picker
      await fetchAgencies();
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iteration 19 — fetch fuzzy reconciliation overview for admin/backoffice badge
  useEffect(() => {
    if (!hasRole(["admin", "backoffice"])) return;
    axios.get(`${API_URL}/reconciliation/fuzzy-suggestions`, { params: { min_score: 80, top_n: 1 } })
      .then((res) => setFuzzyStats({
        auto_resolvable_count: res.data.auto_resolvable_count || 0,
        total_groups: res.data.total_groups || 0,
      }))
      .catch(() => {});
  }, [hasRole]);

  const getStatusColor = (status) => {
    const colors = {
      OK: "bg-emerald-50 text-emerald-700 border-emerald-200",
      KO: "bg-red-50 text-red-700 border-red-200",
      Draft: "bg-slate-100 text-slate-700 border-slate-200",
      Trimis: "bg-blue-50 text-blue-700 border-blue-200",
      "În Procesare": "bg-amber-50 text-amber-700 border-amber-200",
      "Necesită Corecție": "bg-amber-50 text-amber-700 border-amber-200",
      "În Activare": "bg-amber-50 text-amber-700 border-amber-200",
    };
    return colors[status] || "bg-slate-100 text-slate-700 border-slate-200";
  };

  const getStatusIcon = (status) => {
    if (status === "OK") return <CheckCircle2 className="h-4 w-4" />;
    if (status === "KO") return <XCircle className="h-4 w-4" />;
    return <Clock className="h-4 w-4" />;
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const totalContracts = stats?.total_contracts || 0;
  const totalClients = stats?.total_clients || 0;
  const totalSales = stats?.total_sales || 0;
  const byStatus = stats?.by_status || {};
  const byProductKind = stats?.by_product_kind || {};
  const totalAgents = stats?.total_agents || 0;
  const ppcContracts = stats?.ppc_contracts || 0;
  const inActivationOver7d = stats?.in_activation_over_7d || 0;
  const topAgents = stats?.top_agents || [];
  const topAgencies = stats?.top_agencies || [];
  const recentImports = stats?.recent_imports || [];
  const alerts = stats?.alerts || { unassigned_sales: 0, unassigned_contracts: 0 };
  // Iter52 P14m — KPI canonice aliniate cu Salesforce (single source of truth)
  const activatedCount = stats?.activated_count || 0;
  const inActivationCount = stats?.in_activation_count || 0;
  const cancelledCount = stats?.cancelled_count || 0;
  const terminatedCount = stats?.terminated_count || 0;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header with Branding */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <img src={EUROCALL_LOGO} alt="EuroCall" className="h-20 w-auto object-contain" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
            <p className="text-slate-500 mt-1">
              Bun venit, <span className="font-medium text-[#1e3a8a]">{user?.name}</span>!
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isAdminView && agents.length > 0 && (
            <div className="flex items-center gap-2" data-testid="view-as-agent-selector">
              <label className="text-xs uppercase tracking-wide text-slate-500">Vezi ca</label>
              <select
                value={asAgentId}
                onChange={(e) => {
                  const v = e.target.value;
                  setAsAgentId(v);
                  fetchStats(v);
                }}
                className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:border-[#1e3a8a] focus:outline-none"
                data-testid="view-as-agent-select"
              >
                <option value="">— Toți agenții —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.full_name || "Agent fără nume"}
                    {a.primary_marca_code ? ` · ${a.primary_marca_code}` : ""}
                  </option>
                ))}
              </select>
              {asAgentId && (
                <span
                  className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase text-amber-900"
                  data-testid="view-as-active-badge"
                >
                  filtrat
                </span>
              )}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => { toast.info("Se recalculează statisticile — poate dura ~1 minut"); fetchStats(undefined, true); }}
            className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white"
            data-testid="refresh-stats"
            title={stats?.cached_at ? `Date calculate la ${new Date(stats.cached_at).toLocaleString("ro-RO")}. Apasă pentru recalculare.` : "Recalculează statisticile"}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizează
          </Button>
          <Link to="/sales/new">
            <Button className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="new-sale-btn">
              <Plus className="h-4 w-4 mr-2" />
              Vânzare Nouă
            </Button>
          </Link>
        </div>
      </div>

      {/* Iter53 P25 — Role-aware dashboard widget (Agent / Partener / Admin)
          Iter53 P26 — sincronizat cu „Vezi ca" agent dropdown (asAgentId) pentru consistență */}
      <RoleDashboardWidget user={user} agents={agents} agencies={agencies} viewAsAgentId={asAgentId} />

      {/* Situație pe canal × produs (obiectiv vs realizat după CSD) */}
      {isAdminView && <ChannelProductMatrix />}

      {/* Situație pe agenții: Încărcate vs CSD, lunar/trimestrial (admin/manager/backoffice) */}
      {hasRole(["admin", "backoffice", "manager"]) && <AgencyProgressCards />}

      {/* Operational Alerts (admin/backoffice only) */}
      {isAdminView && (alerts.unassigned_sales > 0 || alerts.unassigned_contracts > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {alerts.unassigned_sales > 0 && (
            <Link to="/reconciliation" data-testid="widget-unassigned-sales">
              <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow cursor-pointer bg-amber-50/40">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Vânzări neasociate
                      </p>
                      <p className="text-3xl font-bold text-amber-900 mt-1">
                        {alerts.unassigned_sales}
                      </p>
                      <p className="text-xs text-amber-700/80 mt-1">Necesită reconciliere manuală</p>
                      {fuzzyStats.auto_resolvable_count > 0 && (
                        <Link to="/reconciliation/fuzzy" data-testid="dashboard-fuzzy-badge" onClick={(e) => e.stopPropagation()}>
                          <Badge className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px]">
                            {fuzzyStats.auto_resolvable_count} sugestii fuzzy ≥80
                          </Badge>
                        </Link>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 text-amber-600" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
          {alerts.unassigned_contracts > 0 && (
            <Link to="/sales?unassigned_only=true" data-testid="widget-unassigned-contracts">
              <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow cursor-pointer bg-amber-50/40">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Contracte neasociate
                      </p>
                      <p className="text-3xl font-bold text-amber-900 mt-1">
                        {alerts.unassigned_contracts}
                      </p>
                      <p className="text-xs text-amber-700/80 mt-1">Necesită reconciliere manuală</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-amber-600" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>
      )}

      {/* Iter52 P19 — Rework Queue mutat la top (alături de unassigned alerts pentru focus operațional) */}
      {isAdminView && <ReworkQueueWidget user={user} compactLimit={7} />}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6" data-testid="dashboard-kpi-grid">
        <Card className="border-l-4 border-l-[#1e3a8a] hover:shadow-lg transition-shadow" data-testid="kpi-total-contracts">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Contracte</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="stat-total-contracts">
                  {totalContracts}
                </p>
              </div>
              <div className="h-10 w-10 md:h-14 md:w-14 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center">
                <FileText className="h-7 w-7 text-[#1e3a8a]" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <span className="text-emerald-600 font-medium">Active</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow" data-testid="kpi-total-clients">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Clienți</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="stat-total-clients">{totalClients}</p>
              </div>
              <div className="h-10 w-10 md:h-14 md:w-14 rounded-full bg-emerald-50 flex items-center justify-center">
                <Users className="h-7 w-7 text-emerald-600" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-sm">
              <Activity className="h-4 w-4 text-emerald-500" />
              <span className="text-emerald-600 font-medium">Înregistrați</span>
            </div>
          </CardContent>
        </Card>

        {/* PPC Contracts */}
        <Link to="/sales?supplier=PPC" data-testid="widget-ppc-contracts">
          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Contracte PPC</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{ppcContracts}</p>
                </div>
                <div className="h-10 w-10 md:h-14 md:w-14 rounded-full bg-blue-50 flex items-center justify-center">
                  <Zap className="h-7 w-7 text-blue-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 text-sm text-blue-600 font-medium">
                <ArrowRight className="h-3 w-3" />
                <span>Vezi listă</span>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* În Activare > 7 zile */}
        <Link
          to="/sales?status=%C3%8En%20Activare&activation_days_min=7"
          data-testid="widget-in-activation-7d"
        >
          <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">În Activare {">"} 7 zile</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{inActivationOver7d}</p>
                </div>
                <div className="h-10 w-10 md:h-14 md:w-14 rounded-full bg-orange-50 flex items-center justify-center">
                  <Clock className="h-7 w-7 text-orange-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 text-sm text-orange-600 font-medium">
                <Calendar className="h-3 w-3" />
                <span>Necesită atenție</span>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* SF Business Status KPI Row (Iter52 P14m) — single source of truth */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-sf-status-row">
        <Link to="/sales?business_status=activated" data-testid="kpi-activated">
          <Card className="border-l-4 border-l-emerald-500 hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Activate</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="stat-activated">{activatedCount}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
              <p className="text-xs text-emerald-700 mt-3 font-medium">Active în SF</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/sales?business_status=signed" data-testid="kpi-in-activation">
          <Card className="border-l-4 border-l-amber-500 hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">În Activare</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="stat-in-activation">{inActivationCount}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-amber-50 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-3 font-medium">Semnate, în curs</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/sales?business_status=cancelled" data-testid="kpi-cancelled">
          <Card className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Anulate</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="stat-cancelled">{cancelledCount}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-red-50 flex items-center justify-center">
                  <Ban className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-red-700 mt-3 font-medium">Anulate înainte de activare</p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/sales?business_status=terminated" data-testid="kpi-terminated">
          <Card className="border-l-4 border-l-slate-500 hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Reziliate</p>
                  <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" data-testid="stat-terminated">{terminatedCount}</p>
                </div>
                <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-slate-100 flex items-center justify-center">
                  <PowerOff className="h-6 w-6 text-slate-600" />
                </div>
              </div>
              <p className="text-xs text-slate-700 mt-3 font-medium">Terminate după activare</p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Business Status Distribution + KPI cards (FAZA 3+) */}
      <BusinessStatusDistribution />

      {/* Faza B v2 — Distribuție Status General (8 statuses grid + KPI sub-bar) */}
      <GeneralStatusDistribution />

      {/* Status + Top Performers Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a8a]">
              <Activity className="h-5 w-5" />
              Distribuție Status
            </CardTitle>
            <CardDescription>Starea contractelor în sistem</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(byStatus).length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nu există contracte înregistrate</p>
              ) : (
                Object.entries(byStatus).map(([status, count]) => (
                  <Link
                    key={status}
                    to={`/sales?status=${encodeURIComponent(status)}`}
                    className="block hover:bg-slate-50 rounded-md px-2 py-1 -mx-2 transition-colors"
                    data-testid={`status-row-${status.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={`${getStatusColor(status)} border px-3 py-1`}>
                          {getStatusIcon(status)}
                          <span className="ml-1.5">{status}</span>
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#1e3a8a] rounded-full transition-all duration-500"
                            style={{
                              width: `${totalContracts > 0 ? (count / totalContracts) * 100 : 0}%`,
                            }}
                          ></div>
                        </div>
                        <span className="font-semibold text-slate-900 w-8 text-right">{count}</span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Iter51 Etapa 14 — Distribuție Tip produs */}
        <Card className="card-hover" data-testid="product-kind-widget">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#1e3a8a]" />
              Distribuție Tip produs
            </CardTitle>
            <CardDescription>Vânzări grupate după tipul produsului (EE/Gaz/VAS/HVAS/Combo)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(byProductKind).length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nu există încă vânzări clasificate</p>
              ) : (
                Object.entries(byProductKind).sort((a, b) => b[1] - a[1]).map(([kind, count]) => {
                  const totalPk = Object.values(byProductKind).reduce((s, v) => s + v, 0) || 1;
                  // Iter52 P14y — accept BOTH legacy slug keys (ee/gaz/...) and current
                  // canonical labels returned by /api/dashboard/stats (EE / Gaz / Combo EE + VAS, etc.)
                  const labels = {
                    "EE":               { label: "EE",               cls: "bg-amber-100 text-amber-800 border-amber-200",  bar: "bg-amber-500"  },
                    "Gaz":              { label: "Gaz",              cls: "bg-orange-100 text-orange-800 border-orange-200",bar: "bg-orange-500" },
                    "VAS":              { label: "VAS",              cls: "bg-indigo-100 text-indigo-800 border-indigo-200",bar: "bg-indigo-500" },
                    "HVAS":             { label: "HVAS",             cls: "bg-purple-100 text-purple-800 border-purple-200",bar: "bg-purple-500" },
                    "Combo EE + VAS":   { label: "Combo EE + VAS",   cls: "bg-gradient-to-r from-amber-100 to-indigo-100 text-amber-900 border-amber-200", bar: "bg-gradient-to-r from-amber-500 to-indigo-500" },
                    "Combo Gaz + VAS":  { label: "Combo Gaz + VAS",  cls: "bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-900 border-orange-200", bar: "bg-gradient-to-r from-orange-500 to-indigo-500" },
                    // legacy slug fallbacks (păstrate pentru compatibilitate)
                    ee: { label: "EE",                cls: "bg-amber-100 text-amber-800 border-amber-200",  bar: "bg-amber-500"  },
                    gaz: { label: "Gaz",              cls: "bg-orange-100 text-orange-800 border-orange-200",bar: "bg-orange-500" },
                    vas: { label: "VAS",              cls: "bg-indigo-100 text-indigo-800 border-indigo-200",bar: "bg-indigo-500" },
                    hvas: { label: "HVAS",            cls: "bg-purple-100 text-purple-800 border-purple-200",bar: "bg-purple-500" },
                    combo_ee_vas: { label: "Combo EE+VAS",  cls: "bg-gradient-to-r from-amber-100 to-indigo-100 text-amber-900 border-amber-200", bar: "bg-gradient-to-r from-amber-500 to-indigo-500" },
                    combo_gaz_vas:{ label: "Combo Gaz+VAS", cls: "bg-gradient-to-r from-orange-100 to-indigo-100 text-orange-900 border-orange-200", bar: "bg-gradient-to-r from-orange-500 to-indigo-500" },
                    unset: { label: "Neclasificat",   cls: "bg-slate-100 text-slate-700 border-slate-200",   bar: "bg-slate-400"  },
                  };
                  const t = labels[kind] || labels.unset;
                  return (
                    <div key={kind} className="flex items-center justify-between" data-testid={`product-kind-row-${kind}`}>
                      <Badge className={`${t.cls} border px-3 py-1`}>{t.label}</Badge>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${t.bar} rounded-full transition-all duration-500`} style={{ width: `${(count / totalPk) * 100}%` }} />
                        </div>
                        <span className="font-semibold text-slate-900 w-8 text-right">{count}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Iter51 E25b — Comparație lunară pe tip produs */}
        <ProductKindComparison />

        <Card className="hover:shadow-lg transition-shadow" data-testid="widget-top-agents">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#1e3a8a]">
                <Award className="h-5 w-5" />
                Top Agenți — Luna curentă
              </CardTitle>
              <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="agent-perspective-toggle">
                <button
                  type="button"
                  onClick={() => fetchStatsWithPerspective("commercial")}
                  className={`px-2.5 py-1 font-medium transition-colors ${agentPerspective === "commercial" ? "bg-[#1e3a8a] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid="perspective-commercial-btn"
                  title="Semnatarul real (owner comercial CRM)"
                >
                  Real
                </button>
                <button
                  type="button"
                  onClick={() => fetchStatsWithPerspective("sf")}
                  className={`px-2.5 py-1 font-medium transition-colors ${agentPerspective === "sf" ? "bg-[#1e3a8a] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid="perspective-sf-btn"
                  title="Cum apare în Salesforce (informativ)"
                >
                  SF
                </button>
              </div>
            </div>
            <CardDescription>
              {agentPerspective === "sf"
                ? "Perspectiva Salesforce — cum apar agenții raportați în SF"
                : "Semnatarul real — cei mai performanți agenți în luna curentă"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {topAgents.length === 0 ? (
              <p className="text-slate-500 text-center py-8">
                Nu există vânzări în luna curentă
              </p>
            ) : (
              <div className="space-y-3">
                {topAgents.map((agent, idx) => (
                  <Link
                    key={agent.id}
                    to={`/sales?agent_id=${agent.id}`}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50 transition-colors"
                    data-testid={`top-agent-${agent.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0
                            ? "bg-amber-100 text-amber-700"
                            : idx === 1
                            ? "bg-slate-200 text-slate-700"
                            : idx === 2
                            ? "bg-orange-100 text-orange-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {agent.name || agent.email}
                        </p>
                        <p className="text-xs text-slate-500">{agent.email}</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="font-semibold">
                      {agent.count}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Iter52 P19 — Rework widget mutat sus (after operational alerts). */}

      {/* Iter52 P13h — Incomplete sales widget (BO needs to classify) */}
      <IncompleteSalesWidget />

      {/* Top Agencies + Recent Imports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="hover:shadow-lg transition-shadow" data-testid="widget-top-agencies">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a8a]">
              <Building2 className="h-5 w-5" />
              Top Agenții — Luna curentă
            </CardTitle>
            <CardDescription>Cele mai active agenții în luna curentă</CardDescription>
          </CardHeader>
          <CardContent>
            {topAgencies.length === 0 ? (
              <p className="text-slate-500 text-center py-8">Nu există vânzări în luna curentă</p>
            ) : (
              <div className="space-y-3">
                {topAgencies.map((agency, idx) => (
                  <Link
                    key={agency.id}
                    to={`/sales?agency_id=${agency.id}`}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50 transition-colors"
                    data-testid={`top-agency-${agency.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          idx === 0
                            ? "bg-amber-100 text-amber-700"
                            : idx === 1
                            ? "bg-slate-200 text-slate-700"
                            : idx === 2
                            ? "bg-orange-100 text-orange-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {idx + 1}
                      </div>
                      <p className="text-sm font-medium text-slate-900">{agency.name}</p>
                    </div>
                    <Badge variant="secondary" className="font-semibold">
                      {agency.count}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Imports (admin/backoffice only) */}
        {isAdminView ? (
          <Card className="hover:shadow-lg transition-shadow" data-testid="widget-recent-imports">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a8a]">
                <Upload className="h-5 w-5" />
                Ultimele Importuri
              </CardTitle>
              <CardDescription>Ultimele 5 importuri Excel procesate</CardDescription>
            </CardHeader>
            <CardContent>
              {recentImports.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Nu există importuri recente</p>
              ) : (
                <div className="space-y-3">
                  {recentImports.map((imp) => (
                    <div
                      key={imp.id}
                      className="flex items-start justify-between p-2 rounded-md hover:bg-slate-50 transition-colors"
                      data-testid={`recent-import-${imp.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {imp.source}
                          </Badge>
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {imp.filename || "—"}
                          </p>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {imp.imported_at
                            ? new Date(imp.imported_at).toLocaleString("ro-RO", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "—"}
                        </p>
                      </div>
                      <div className="text-right text-xs space-y-0.5 ml-3">
                        <p className="text-emerald-700">+{imp.rows_created || 0} create</p>
                        <p className="text-blue-700">↻{imp.rows_updated || 0} actualizate</p>
                        {(imp.rows_errors || 0) > 0 && (
                          <p className="text-red-700">⚠{imp.rows_errors} erori</p>
                        )}
                      </div>
                    </div>
                  ))}
                  <Link to="/import" className="block pt-2">
                    <Button variant="outline" size="sm" className="w-full">
                      <ArrowRight className="h-3 w-3 mr-1" />
                      Vezi toate importurile
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1e3a8a]">
                <Zap className="h-5 w-5" />
                Acțiuni Rapide
              </CardTitle>
              <CardDescription>Accesează rapid funcționalitățile principale</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/sales/new">
                  <Button
                    variant="outline"
                    className="w-full h-24 flex-col gap-2 hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-colors"
                  >
                    <Plus className="h-6 w-6" />
                    <span>Vânzare Nouă</span>
                  </Button>
                </Link>
                <Link to="/clients">
                  <Button
                    variant="outline"
                    className="w-full h-24 flex-col gap-2 hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-colors"
                  >
                    <Users className="h-6 w-6" />
                    <span>Clienți</span>
                  </Button>
                </Link>
                <Link to="/sales">
                  <Button
                    variant="outline"
                    className="w-full h-24 flex-col gap-2 hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-colors"
                  >
                    <FileText className="h-6 w-6" />
                    <span>Vânzări</span>
                  </Button>
                </Link>
                <Link to="/agencies">
                  <Button
                    variant="outline"
                    className="w-full h-24 flex-col gap-2 hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-colors"
                  >
                    <Building2 className="h-6 w-6" />
                    <span>Agenții</span>
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* System Info */}
      <Card className="bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] text-white">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">EuroCall CRM</h3>
                <p className="text-blue-100 text-sm">Sistem de management contracte energie</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-2xl font-bold">{totalSales}</p>
                <p className="text-blue-100">Vânzări</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{totalAgents}</p>
                <p className="text-blue-100">Agenți</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{Object.keys(byStatus).length}</p>
                <p className="text-blue-100">Statusuri</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
